import { generateUrl } from "@repo/fika-shared/src/lib/slug.js"
import { useParams } from "@solidjs/router"
import { getTitle } from "@/lib/get-title"
import { createMatrixParams } from "@/lib/matrix-params"

const BLOG_BASE_PATH = "/blog" as const

export const FILTER = {
  spam: { value: "spam", iconName: "shield-alert", hue: "rose" },
  archived: { value: "archived", iconName: "archive", hue: "orange" },
  sent: { value: "sent", iconName: "send", hue: "indigo" },
  published: { value: "published", iconName: "book-up", hue: "emerald" },
  draft: { value: "draft", iconName: "pencil", hue: "sand" },
} as const

export const INTERVAL = {
  today: "today",
  yesterday: "yesterday",
  "1d": "24 hours",
  "7d": "7 days",
  "30d": "30 days",
  "90d": "90 days",
} as const

export type Filter = keyof typeof FILTER | undefined
export type Interval = keyof typeof INTERVAL
export type SortField = "referrer" | "country" | "channel"

export interface ListParams {
  filter: Filter
  tag: string | undefined
  query: string
  after: string
  before: string
}

export const LIST_DEFAULTS: ListParams = {
  filter: undefined,
  tag: undefined,
  query: "",
  after: "",
  before: "",
}

export interface SubscriberListParams {
  query: string
  tierId: string | undefined
  formId: string | undefined
  after: string
  before: string
}

export const SUBSCRIBER_LIST_DEFAULTS: SubscriberListParams = {
  query: "",
  tierId: undefined,
  formId: undefined,
  after: "",
  before: "",
}

type FieldParams = { page: number }
type StatsParams = {
  interval: Interval
  country: FieldParams
  referrer: FieldParams
  channel: FieldParams
}

const STATS_DEFAULTS: StatsParams = {
  interval: "1d",
  country: { page: 1 },
  referrer: { page: 1 },
  channel: { page: 1 },
}

export function createBlogRouter() {
  const params = useParams()
  const list = createMatrixParams(() => params.listParams, LIST_DEFAULTS)
  const subscriberList = createMatrixParams(
    () => params.subscriberListParams,
    SUBSCRIBER_LIST_DEFAULTS,
  )
  const stats = createMatrixParams(() => params.statsParams, STATS_DEFAULTS)

  function paths(listParams?: Partial<ListParams>) {
    const url = `${BLOG_BASE_PATH}${list.makeURL(listParams)}`

    return {
      url,
      new: () => `${url}/new`,
      settings: () => `${url}/settings`,
      forms: () => `${url}/forms`,
      formsNew: () => `${url}/forms/new`,
      formsEdit: (formId: string) => `${url}/forms/${formId}`,
      audience: (subscriberListParams?: Partial<SubscriberListParams>) =>
        `${url}/audience${subscriberList.makeURL(subscriberListParams)}`,
      stats: (statsParams?: Partial<StatsParams>) => {
        return `${url}/stats${stats.makeURL(statsParams)}`
      },
      post: (postModel?: { id: string; title: string | null }) => {
        const id = postModel
          ? generateUrl(getTitle(postModel.title), postModel.id)
          : params.postId
        const postUrl = `${[url, "post", id].filter(Boolean).join("/")}`

        return {
          url: postUrl,
          settings: () => `${postUrl}/settings`,
          audience: () => `${postUrl}/audience`,
          publish: () => `${postUrl}/publish`,
          publishShare: () => `${postUrl}/publish/share`,
          update: () => `${postUrl}/update`,
          share: () => `${postUrl}/share`,
          notes: () => ({
            url: `${postUrl}/notes`,
          }),
          proofread: () => {
            const proofreadUrl = `${postUrl}/proofread`
            return {
              url: proofreadUrl,
              principle: (principle: string) => `${proofreadUrl}/${principle}`,
            }
          },
          versions: (versionId?: string) =>
            [postUrl, "versions", versionId].filter(Boolean).join("/"),
          translations: () => `${postUrl}/translations`,
        }
      },
    }
  }

  return {
    paths,
    listParams: list.matrixParams,
    statsParams: stats.matrixParams,
    subscriberListParams: subscriberList.matrixParams,
  }
}
